import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useHomepageOrbTheme } from "@/hooks/useHomepageOrbTheme";
import { ChevronDown } from "lucide-react";
import logoWhiteAsset from "@/assets/logo-white.webp.asset.json";
const logoWhite = logoWhiteAsset.url;
import barsMenuAsset from "@/assets/bars-white-menu.png.asset.json";
const barsMenu = barsMenuAsset.url;
import barsWhiteAsset from "@/assets/bars-white.png.asset.json";
const barsWhite = barsWhiteAsset.url;
import barsLogoAsset from "@/assets/bars-white-transparent.webp.asset.json";
const barsLogo = barsLogoAsset.url;
import BtnArrow from "@/components/ui/btn-arrow";
import LogoBars from "@/components/brand/LogoBars";
import SimplifiedMenuOverlay from "@/components/SimplifiedMenuOverlay";
import {
  KELVIN_STROKE_COLORS,
  MENU_GLOW_CLOSE_GRADIENT,
  MENU_GLOW_GRADIENTS_DARK,
  MENU_GLOW_GRADIENTS_LIGHT,
  MENU_GLOW_PULSE_TRANSITION,
} from "@/lib/kelvinGlow";

// Desktop and mobile menus render exclusively from `menuConfig` below —
// there is a single source of truth for menu labels, anchors, and order.


const primaryLinks = [
  { label: "Home", href: "/" },
  { label: "Explore the codelumen System", href: "/system" },
  { label: "Capabilities", href: "/solutions" },
  { label: "Our Work", href: "/work" },
  { label: "Schedule a Labs Session", href: "/labs" },
  { label: "About codelumen", href: "/about" },
  { label: "Contact", href: "/contact" },
  { label: "Talent", href: "/talent" },
];

// Editorial mega-menu config for desktop/iPad (≥md)
type MenuKey = "home" | "system" | "solutions" | "work" | "labs" | "about" | "contact" | "talent";

type MenuSubLink = {
  label: string;
  anchor: string;
  description?: string;
};

type MenuEntry = {
  key: MenuKey;
  label: string;
  href: string;
  eyebrow: string;
  intro?: string;
  subLinks: MenuSubLink[];
  cta: { label: string; href: string };
};

const menuConfig: MenuEntry[] = [
  {
    key: "home",
    label: "Home",
    href: "/",
    eyebrow: "codelumen",
    intro: "One system, end-to-end execution. Start here and navigate the full story.",
    subLinks: [
      { label: "Intro", anchor: "#hero", description: "The codelumen opening. Lighting, decoded." },
      { label: "What We Do", anchor: "#what-we-do", description: "The integrated practice in a single view." },
      { label: "The Problem", anchor: "#problem", description: "Why fragmented lighting delivery keeps failing." },
      { label: "The System", anchor: "#system-teaser", description: "Design, products, controls, and platform, connected." },
      { label: "The Value", anchor: "#value", description: "Better health, better spaces, better results." },
      { label: "Selected Work", anchor: "#projects", description: "A snapshot of recent projects across markets." },
      { label: "codelumenLabs", anchor: "#labs", description: "Experience the system in person at the lab." },
      { label: "Vision", anchor: "#vision", description: "Why codelumen exists." },
      { label: "Contact", anchor: "#cta", description: "Start a project conversation." },
    ],

    cta: { label: "Back to Home", href: "/" },
  },
  {
    key: "system",
    label: "The System",
    href: "/system",
    eyebrow: "The four layers",
    intro: "One integrated lighting system. One accountable team.",
    subLinks: [
      { label: "Overview", anchor: "#hero", description: "The codelumen system in one view." },
      { label: "System Spine", anchor: "#spine", description: "Seven stages. One continuous spine of accountability." },
      { label: "Four Layers", anchor: "#layers", description: "Design, products, controls, and platform, connected." },
      { label: "Layer Deep Dives", anchor: "#layers-deep", description: "How each layer operates in practice." },
      { label: "Performance & Expression", anchor: "#fixture-strategy", description: "How fixtures translate design intent into built light." },
      { label: "Responsibility", anchor: "#responsibility", description: "Who owns what, at every stage." },
      { label: "Project Journey", anchor: "#process", description: "How a project moves through the system, stage by stage." },
      { label: "How Engagements Begin", anchor: "#engagement", description: "Where a project can enter the system." },
      { label: "Proof", anchor: "#evidence", description: "Full Ecosystem projects delivered end to end." },
      { label: "Start a Project", anchor: "#cta", description: "Begin a conversation with the codelumen team." },
    ],
    cta: { label: "Explore the codelumen System", href: "/system" },
  },
  {
    key: "solutions",
    label: "Capabilities",
    href: "/solutions",
    eyebrow: "What we deliver",
    intro: "Four disciplines. One connected lighting ecosystem.",
    subLinks: [
      { label: "Overview", anchor: "#hero", description: "Six capabilities working as one connected system." },
      { label: "Lighting Design", anchor: "#lighting-design", description: "Concept, photometrics, layering, and scene strategy." },
      { label: "Product Engineering", anchor: "#product-engineering", description: "Fixtures engineered for the architecture." },
      { label: "Controls Integration", anchor: "#controls-integration", description: "Zoning, scenes, protocols, and behavior." },
      { label: "Project Execution", anchor: "#project-execution", description: "The codelumen Platform, from submittal to commissioning." },
      { label: "Commissioning", anchor: "#commissioning", description: "Aiming, tuning, and verification in the space." },
      
      { label: "Breadth of Experience", anchor: "#breadth", description: "Sectors and project types codelumen delivers across." },
    ],
    cta: { label: "Explore Capabilities", href: "/solutions" },
  },
  {
    key: "work",
    label: "Our Work",
    href: "/work",
    eyebrow: "Selected projects by type",
    intro: "Examples of how the system is translated into built work, organized by project type.",
    subLinks: [
      { label: "Overview", anchor: "#hero", description: "All selected projects, unfiltered." },
      { label: "Featured Projects", anchor: "?service=Featured", description: "A curated selection across markets and project types." },
      { label: "Hospitality", anchor: "?filter=Hospitality", description: "Hotels, restaurants, bars, and guest-facing environments." },
      { label: "Office", anchor: "?filter=Office Space", description: "Workplace and corporate interiors." },
      { label: "High-End Residential", anchor: "?filter=Upscale Residential", description: "Private residences and luxury homes." },
      { label: "Civic", anchor: "?filter=Public Works", description: "Public works, monuments, and civic infrastructure." },
      { label: "Retail", anchor: "?filter=Retail", description: "Stores, galleries, and brand environments." },
      { label: "Urban", anchor: "?filter=Urban", description: "Mixed-use districts and urban placemaking." },
    ],
    cta: { label: "View Our Work", href: "/work" },
  },
  {
    key: "labs",
    label: "Labs",
    href: "/labs",
    eyebrow: "codelumenLabs",
    intro: "A private B2B project experience, curated for project teams with a current or upcoming project.",
    subLinks: [
      { label: "Overview", anchor: "#hero", description: "codelumenLabs at a glance." },
      { label: "Why the Lab", anchor: "#why", description: "Drawings describe light. The Lab lets your team experience it." },
      { label: "Inside the Lab", anchor: "#evaluate", description: "Four integrated experiences: design, product, controls, and execution." },
      { label: "The Dark Room", anchor: "#darkroom", description: "Experience a full day of scenes in minutes." },
      { label: "Discovery", anchor: "#discovery", description: "How the visit becomes a decision-ready evaluation for your project." },
      { label: "Book Your Experience", anchor: "#booking", description: "In-person visit or virtual codelumenLabs presentation." },
      { label: "FAQ", anchor: "#faq", description: "Who codelumenLabs is for and how a visit works." },
    ],
    cta: { label: "Explore codelumenLabs", href: "/labs" },
  },
  {
    key: "about",
    label: "About",
    href: "/about",
    eyebrow: "About codelumen",
    intro: "Why codelumen exists, what it believes, and the mission that guides how it operates.",
    subLinks: [
      { label: "Purpose", anchor: "#purpose", description: "Empower humanity through light." },
      { label: "Why Artificial Light Matters", anchor: "#why-light", description: "Light as one of humanity's most powerful technologies." },
      { label: "Our Commitment", anchor: "#commitment", description: "Mission before short-term gain." },
      { label: "The Origin", anchor: "#origin", description: "The problem that became our mission." },
      { label: "Evolution", anchor: "#evolution", description: "Capabilities added in service of the mission." },
      { label: "Leadership", anchor: "#founders", description: "Guided by engineering, design, and accountability." },
      { label: "Principles", anchor: "#principles", description: "How we decide." },
      { label: "Human Responsibility", anchor: "#human", description: "Design for the people who live with the light." },
      { label: "Collaboration", anchor: "#collaboration", description: "How we work with architects, developers, and contractors." },
      { label: "Organizations We Support", anchor: "#organizations", description: "Communities and causes codelumen stands behind." },
      { label: "The Horizon", anchor: "#horizon", description: "Where the mission is heading." },
      { label: "Start a Conversation", anchor: "#cta", description: "Bring codelumen into your next project." },
    ],
    cta: { label: "About codelumen", href: "/about" },
  },
  {
    key: "contact",
    label: "Contact",
    href: "/contact",
    eyebrow: "Start the conversation",
    intro: "Choose the path that fits your project or question.",
    subLinks: [
      { label: "Overview", anchor: "#hero", description: "Choose the right path to reach the team." },
      { label: "Request a Project Review", anchor: "#form", description: "Start a conversation about a new project, an active scope, or drawings to review." },
      { label: "Book a Private Demo", anchor: "/labs#booking", description: "Schedule a codelumenLabs session or virtual walkthrough." },
      { label: "General Contact", anchor: "mailto:info@clumen.com", description: "Reach the team directly at info@clumen.com." },

    ],
    cta: { label: "Contact codelumen", href: "/contact" },
  },
  {
    key: "talent",
    label: "Talent",
    href: "/talent",
    eyebrow: "Join codelumen",
    intro: "Build the future of lighting with us. We are always looking for people who think across disciplines and care about details.",
    subLinks: [
      { label: "Intro", anchor: "#hero", description: "Why codelumen is building a new kind of lighting practice." },
      { label: "Why codelumen", anchor: "#why", description: "What makes this practice different for the people who join it." },
      { label: "Where You'll Thrive", anchor: "#thrive", description: "The kind of work and problems you'll take on here." },
      { label: "Talent Areas", anchor: "#disciplines", description: "Design, engineering, controls, platform, operations, and growth." },
      { label: "Culture", anchor: "#culture", description: "How the team operates day to day." },
      { label: "Philosophy", anchor: "#philosophy", description: "What we believe about craft, ownership, and outcomes." },
      { label: "Open Opportunities", anchor: "#opportunities", description: "Where the team is actively looking to grow." },
      { label: "Join the Network", anchor: "#form", description: "Submit your profile. We reach out when there is a fit." },
    ],
    cta: { label: "Join the Talent Network", href: "/talent" },
  },
];

// (Resting state of the desktop mega-menu's right panel now uses a single
// "Bring codelumen in early" banner with one Request a Project Review CTA,
// so the legacy `menuHomeIntents` array has been removed.)
//
// The desktop mega-menu now renders every entry through one shared
// "system-style" template (eyebrow → intro → divided row list → CTA),
// so the per-sector thumbnail map previously used by the "work" grid
// has been removed.


const secondaryLinks = [
  { label: "Lighting Design", href: "/solutions#lighting-design" },
  { label: "Product Engineering", href: "/solutions#product-engineering" },
  { label: "Controls Integration", href: "/solutions#controls-integration" },
  { label: "Lighting Coordination Platform", href: "/solutions#project-execution" },
];

const sidebarVariants = {
  closed: { x: "100%" },
  open: {
    x: 0,
    transition: { duration: 0.6, ease: [0.76, 0, 0.24, 1] as [number, number, number, number] },
  },
  exit: {
    x: "100%",
    transition: { duration: 0.45, ease: [0.76, 0, 0.24, 1] as [number, number, number, number] },
  },
};

const linkContainerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.07, delayChildren: 0.25 },
  },
  exit: {
    transition: { staggerChildren: 0.03, staggerDirection: -1 },
  },
};

const linkItemVariants = {
  hidden: { opacity: 0, y: 24, x: -10 },
  visible: {
    opacity: 1,
    y: 0,
    x: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
  exit: {
    opacity: 0,
    y: -12,
    transition: { duration: 0.2 },
  },
};

const NAV_LOGO_SEQUENCE_MS = 1500;
// Menu closes shortly after a tap so the destination page becomes visible
// almost immediately. LogoBars runs its own internal cascade keyed on
// triggerId, so we don't need to hold the overlay for the full sequence.
const NAV_LOGO_VISIBLE_CLOSE_DELAY_MS = 360;
const NAV_LOGO_SEQUENCE_STEPS = [1, 2, 3, 4] as const;
// Per-step interval. Must be ≥ LogoBars fade-in (~280ms) so each bar reaches
// near-full brightness before the next fires — otherwise the narrower bars
// (esp. bar 2 at 3px) get visually swallowed by the wider bars catching up.
const NAV_LOGO_STEP_MS = 220;

const ecosystemPhrases = [
  "One integrated lighting practice. One point of accountability.",
  "Lighting design, product engineering, controls, and field coordination, connected end to end.",
  "What gets designed is what gets installed.",
  "Fewer coordination gaps. Better outcomes. One number to call.",
  "Custom fixtures when catalog options don't fit the project.",
  "Controls planned with the design, not added at commissioning.",
  "Architectural lighting, from first concept to final commissioning.",
  "Architectural lighting strategy from concept to completion.",
  "Design, products, controls, and execution as one system.",
  "Bring lighting in early. Avoid compromises later.",
];

// Kept temporarily for direct visual comparison while the simplified menu is
// reviewed. The legacy markup is not rendered in the proposed concept.
const SHOW_LEGACY_MENU = false;

const Header = () => {
  const homeOrbTheme = useHomepageOrbTheme();
  const reduceMotion = useReducedMotion();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuOpenRef = useRef(false);
  // Hide the "Menu" caption after the user first hovers/taps the orb on this
  // page load. Resets on a real refresh (initial state false), but persists
  // across SPA route changes so it doesn't reappear every time the menu closes.
  const [menuLabelDismissed, setMenuLabelDismissed] = useState(false);
  const dismissMenuLabel = useCallback(() => setMenuLabelDismissed(true), []);
  const [overDark, setOverDark] = useState(true);
  const [overImage, setOverImage] = useState(false);
  // Per-logo sampling: decides whether the wordmark itself should invert to
  // dark, independent of the global header vote. Needed because galleries
  // can fill the center/right of the viewport while the logo sits over a
  // white page margin (or vice versa).
  const [logoOverLight, setLogoOverLight] = useState(false);
  const logoOverLightRef = useRef(false);
  const pendingLogoOverLightRef = useRef<boolean | null>(null);
  const logoRef = useRef<HTMLImageElement | null>(null);
  const [activePhrase, setActivePhrase] = useState(ecosystemPhrases[0]);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(1);
  const phraseIndexRef = useRef(0);
  const prevPhraseRef = useRef(ecosystemPhrases[0]);
  const pendingOverDarkRef = useRef<boolean | null>(null);
  const overDarkRef = useRef(true);
  const location = useLocation();
  const navigate = useNavigate();

  const [labsHovered, setLabsHovered] = useState(false);
  const [designHovered, setDesignHovered] = useState(false);
  const [platformHovered, setPlatformHovered] = useState(false);
  const [ecosystemHovered, setEcosystemHovered] = useState(false);
  const [workHovered, setWorkHovered] = useState(false);
  const [solutionsHovered, setSolutionsHovered] = useState(false);
  const [aboutHovered, setAboutHovered] = useState(false);
  const [sidebarBarsVisible, setSidebarBarsVisible] = useState([false, false, false, false]);
  const [hoveredSubBar, setHoveredSubBar] = useState<number | "all" | null>(null);
  const [hoveredPrimaryIndex, setHoveredPrimaryIndex] = useState<number | null>(null);
  // Desktop mega-menu: which primary key is currently shown in the right panel.
  const [activeMenuKey, setActiveMenuKey] = useState<MenuKey>("system");
  // Mobile (<md) accordion: which single primary section is currently expanded.
  // Independent from desktop hover state so the two layouts never interfere.
  const [mobileOpenKey, setMobileOpenKey] = useState<MenuKey | null>(null);
  // Bumped on any mobile nav tap (sublink or per-section CTA). Drives a
  // one-shot 1→2→3→4 illumination of the top-of-menu four-bar mark, so users
  // feel the tap register even though the menu closes moments later.
  const [navLogoSequenceActive, setNavLogoSequenceActive] = useState(false);
  const [navLogoProgress, setNavLogoProgress] = useState(0);
  const [navLogoTriggerId, setNavLogoTriggerId] = useState(0);
  const navLogoSequenceTimerRef = useRef<number | null>(null);
  const navLogoProgressTimerRefs = useRef<number[]>([]);
  const navLogoStartTimerRef = useRef<number | null>(null);
  const delayedMenuCloseTimerRef = useRef<number | null>(null);
  const suppressNextLocationCloseRef = useRef(false);

  const playNavLogoSequence = useCallback(() => {
    // Restartable cascade: every hover/focus/tap replays the 1→2→3→4 illumination.
    // A tiny debounce collapses fast diagonal sweeps into one clean cascade while
    // still guaranteeing every deliberate hover (including rows 5+) gets a
    // visible response.
    navLogoProgressTimerRefs.current.forEach((t) => window.clearTimeout(t));
    navLogoProgressTimerRefs.current = [];
    if (navLogoSequenceTimerRef.current) {
      window.clearTimeout(navLogoSequenceTimerRef.current);
      navLogoSequenceTimerRef.current = null;
    }
    if (navLogoStartTimerRef.current) {
      window.clearTimeout(navLogoStartTimerRef.current);
      navLogoStartTimerRef.current = null;
    }

    // Force visible reset so bars drop before relighting even mid-cascade.
    setNavLogoProgress(0);
    setNavLogoSequenceActive(true);

    navLogoStartTimerRef.current = window.setTimeout(() => {
      // Bump trigger id so the LogoBars remount and animate from 0.
      setNavLogoTriggerId((id) => id + 1);

      NAV_LOGO_SEQUENCE_STEPS.forEach((step, index) => {
        const t = window.setTimeout(() => {
          setNavLogoProgress(step);
        }, index * NAV_LOGO_STEP_MS + 20);
        navLogoProgressTimerRefs.current.push(t);
      });

      navLogoSequenceTimerRef.current = window.setTimeout(() => {
        setNavLogoSequenceActive(false);
        setNavLogoProgress(0);
        navLogoSequenceTimerRef.current = null;
      }, NAV_LOGO_SEQUENCE_MS);

      navLogoStartTimerRef.current = null;
    }, 40);
  }, []);

  useEffect(() => {
    menuOpenRef.current = menuOpen;
  }, [menuOpen]);

  useEffect(() => {
    return () => {
      if (navLogoSequenceTimerRef.current) {
        window.clearTimeout(navLogoSequenceTimerRef.current);
        navLogoSequenceTimerRef.current = null;
      }
      navLogoProgressTimerRefs.current.forEach((timer) => window.clearTimeout(timer));
      navLogoProgressTimerRefs.current = [];
      if (navLogoStartTimerRef.current) {
        window.clearTimeout(navLogoStartTimerRef.current);
        navLogoStartTimerRef.current = null;
      }
      if (delayedMenuCloseTimerRef.current) {
        window.clearTimeout(delayedMenuCloseTimerRef.current);
        delayedMenuCloseTimerRef.current = null;
      }
    };
  }, []);
  // Scroll-spy: which sublink hash on the current page is most-in-view.
  // Stored as the bare anchor (e.g. "#design") so it matches menuConfig 1:1.
  const [activeSublinkHash, setActiveSublinkHash] = useState<string | null>(null);
  const hoverGraceRef = useRef<number | null>(null);
  const mobileNavRef = useRef<HTMLElement | null>(null);
  const mobileEntryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const scrollMobileEntryToTop = useCallback((key: MenuKey, behavior: ScrollBehavior = "smooth") => {
    const nav = mobileNavRef.current;
    const row = mobileEntryRefs.current[key];
    if (!nav || !row) return;

    const navRect = nav.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const topInset = 10;
    const nextTop = nav.scrollTop + (rowRect.top - navRect.top) - topInset;
    const maxTop = Math.max(nav.scrollHeight - nav.clientHeight, 0);

    nav.scrollTo({
      top: Math.min(Math.max(nextTop, 0), maxTop),
      behavior,
    });
  }, []);

  // When a mobile accordion section opens, scroll its row to the top of the
  // mobile menu's scrollable container so the newly-revealed sublinks
  // "cascade up" into view instead of being hidden below the fold.
  useEffect(() => {
    if (!menuOpen || !mobileOpenKey) return undefined;

    const raf = window.requestAnimationFrame(() => {
      scrollMobileEntryToTop(mobileOpenKey, "smooth");
    });
    const settle = window.setTimeout(() => {
      scrollMobileEntryToTop(mobileOpenKey, "auto");
    }, 220);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(settle);
    };
  }, [mobileOpenKey, menuOpen, scrollMobileEntryToTop]);
  // Logo color is driven by a per-element sample taken under the logo's
  // bounding rect (see detect() below). The legacy global formula failed
  // when galleries filled only part of the viewport: the logo would stay
  // white even when sitting over the white page margin.
  const needsInvert = logoOverLight && !menuOpen;

  const resetSubmenus = useCallback(() => {
    setLabsHovered(false);
    setDesignHovered(false);
    setPlatformHovered(false);
    setEcosystemHovered(false);
    setWorkHovered(false);
    setSolutionsHovered(false);
    setAboutHovered(false);
    setHoveredSubBar(null);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    resetSubmenus();
    setSidebarBarsVisible([false, false, false, false]);
    setHoveredPrimaryIndex(null);
    setHoveredSubBar(null);
    setActiveMenuKey("system");
    setMobileOpenKey(null);
    if (hoverGraceRef.current) {
      window.clearTimeout(hoverGraceRef.current);
      hoverGraceRef.current = null;
    }
  }, [resetSubmenus]);

  const holdMenuForNavLogo = useCallback(
    (action?: () => void) => {
      playNavLogoSequence();
      suppressNextLocationCloseRef.current = true;

      if (delayedMenuCloseTimerRef.current) {
        window.clearTimeout(delayedMenuCloseTimerRef.current);
      }

      // Release the scroll lock immediately so the destination page is
      // scrollable the instant the menu overlay starts to close.
      document.body.style.overflow = "";

      action?.();

      delayedMenuCloseTimerRef.current = window.setTimeout(() => {
        suppressNextLocationCloseRef.current = false;
        delayedMenuCloseTimerRef.current = null;
        closeMenu();
      }, NAV_LOGO_VISIBLE_CLOSE_DELAY_MS);
    },
    [closeMenu, playNavLogoSequence],
  );

  const handleSimplifiedRoute = useCallback(
    (targetHref: string) => {
      const samePage = location.pathname === targetHref;
      holdMenuForNavLogo(() =>
        window.requestAnimationFrame(() => {
          if (samePage) {
            import("@/lib/smoothScroll").then((module) => module.scrollRootToTop());
          } else {
            navigate(targetHref);
          }
        }),
      );
    },
    [holdMenuForNavLogo, location.pathname, navigate],
  );

  const handleSimplifiedProjectReview = useCallback(() => {
    holdMenuForNavLogo(() => {
      window.setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("open-consultation", {
            detail: { variant: "consultation", intent: "value_analysis" },
          }),
        );
      }, NAV_LOGO_VISIBLE_CLOSE_DELAY_MS);
    });
  }, [holdMenuForNavLogo]);

  // Cycle to next phrase each time menu opens + stagger bars
  useEffect(() => {
    if (menuOpen) {
      prevPhraseRef.current = ecosystemPhrases[phraseIndexRef.current];
      phraseIndexRef.current = (phraseIndexRef.current + 1) % ecosystemPhrases.length;
      setActivePhrase(ecosystemPhrases[phraseIndexRef.current]);
      setPhraseIndex(phraseIndexRef.current);
      // Stagger sidebar bars
      const delays = [500, 800, 1100, 1400];
      const timers = delays.map((delay, i) =>
        setTimeout(() => {
          setSidebarBarsVisible((prev) => {
            const next = [...prev];
            next[i] = true;
            return next;
          });
        }, delay)
      );
      return () => timers.forEach(clearTimeout);
    }
  }, [menuOpen]);

  // Detect if header is over a dark or light section
  useEffect(() => {
    const getScrollY = () => {
      if (window.scrollY > 0) return window.scrollY;

      const containers = document.querySelectorAll<HTMLElement>("main, [data-scroll-container]");
      let max = 0;
      containers.forEach((container) => {
        if (container.scrollTop > max) max = container.scrollTop;
      });
      return max;
    };

    let lumCanvas: HTMLCanvasElement | null = null;
    let lumCtx: CanvasRenderingContext2D | null = null;
    const luminanceFromImg = (img: HTMLImageElement): number | null => {
      if (!img.complete || img.naturalWidth === 0) return null;
      if (!lumCanvas) {
        lumCanvas = document.createElement("canvas");
        lumCanvas.width = 4;
        lumCanvas.height = 4;
        lumCtx = lumCanvas.getContext("2d", { willReadFrequently: true });
      }
      if (!lumCtx) return null;
      try {
        lumCtx.clearRect(0, 0, 4, 4);
        lumCtx.drawImage(img, 0, 0, 4, 4);
        const data = lumCtx.getImageData(0, 0, 4, 4).data;
        let sum = 0;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 128) continue;
          sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          count++;
        }
        return count > 0 ? sum / count : null;
      } catch {
        return null;
      }
    };

    const overlapsMarkedMediaHero = (headerHeight: number) => {
      const heroes = document.querySelectorAll<HTMLElement>('[data-header-hero="media"]');
      const headerBottom = headerHeight + 8;

      return Array.from(heroes).some((hero) => {
        const rect = hero.getBoundingClientRect();
        if (rect.width < 120 || rect.height < 120) return false;

        const overlapTop = Math.max(rect.top, 0);
        const overlapBottom = Math.min(rect.bottom, headerBottom);
        return overlapBottom - overlapTop > 12;
      });
    };

    const sampleAt = (x: number, y: number): { lum: number | null; isImage: boolean } => {
      const els = document.elementsFromPoint(x, y);
      // First pass: if ANY large img/video/canvas exists under this point
      // (even sitting beneath translucent scrims/gradients), treat as image.
      // This keeps the header transparent over hero photos on mobile, where
      // dimmer overlays would otherwise hide the underlying image.
      let imageHit: { lum: number | null; isImage: boolean } | null = null;
      for (const el of els) {
        if (el.closest("header") || el.tagName === "HEADER") continue;
        const rect = el.getBoundingClientRect();
        if (rect.width < 120 || rect.height < 40) continue;
        if (el.tagName === "IMG") {
          const lum = luminanceFromImg(el as HTMLImageElement);
          imageHit = { lum, isImage: true };
          break;
        }
        if (el.tagName === "VIDEO" || el.tagName === "CANVAS") {
          imageHit = { lum: null, isImage: true };
          break;
        }
        const cs = getComputedStyle(el);
        if (cs.backgroundImage && cs.backgroundImage.startsWith("url")) {
          const inner = el.querySelector("img");
          if (inner) {
            const lum = luminanceFromImg(inner as HTMLImageElement);
            imageHit = { lum, isImage: true };
          } else {
            imageHit = { lum: null, isImage: true };
          }
          break;
        }
      }
      if (imageHit) return imageHit;

      // Second pass: fall back to the topmost solid CSS background.
      for (const el of els) {
        if (el.closest("header") || el.tagName === "HEADER") continue;
        const rect = el.getBoundingClientRect();
        if (rect.width < 120 || rect.height < 40) continue;
        const cs = getComputedStyle(el);
        const bg = cs.backgroundColor;
        if (!bg || bg === "rgba(0, 0, 0, 0)" || bg === "transparent") continue;
        const match = bg.match(/[\d.]+/g);
        if (!match) continue;
        const [r, g, b] = match.map(Number);
        const a = match.length >= 4 ? parseFloat(match[3]) : 1;
        if (a < 0.5) continue;
        return { lum: 0.299 * r + 0.587 * g + 0.114 * b, isImage: false };
      }
      const bodyBg = getComputedStyle(document.body).backgroundColor;
      const m = bodyBg?.match(/[\d.]+/g);
      if (m) {
        const [r, g, b] = m.map(Number);
        return { lum: 0.299 * r + 0.587 * g + 0.114 * b, isImage: false };
      }
      return { lum: null, isImage: false };
    };

    const detect = () => {
      const y = getScrollY();
      setScrolled(y > 40);

      if (!menuOpen) {
        const delta = y - lastScrollY.current;
        // Always show header within ~240px of the document bottom so users
        // never feel "trapped" in the footer on mobile.
        const docHeight = document.documentElement.scrollHeight;
        const viewportH = window.innerHeight;
        const nearBottom = y + viewportH >= docHeight - 240;
        if (y < 80 || nearBottom) {
          setHidden(false);
        } else if (delta > 6) {
          setHidden(true);
        } else if (delta < -6) {
          setHidden(false);
        }
      }
      lastScrollY.current = y;

      // Sample THROUGH the header band, not below it. On mobile, sampling only
      // beneath the bar can hit the next dark section before the header itself
      // has visually cleared the hero image, which makes the header turn black
      // while it is still sitting over photography.
      const headerEl = document.querySelector("header");
      const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 80;
      // Sample directly behind the logo. Independent from the global vote so
      // the wordmark stays legible even when only PART of the viewport behind
      // the header is an image (galleries that fill center/right while the
      // logo sits over a white margin, or vice versa).
      const sampleLogo = () => {
        const logoEl = logoRef.current;
        if (!logoEl) return;
        const lr = logoEl.getBoundingClientRect();
        if (lr.width <= 0 || lr.height <= 0) return;
        const ly = lr.top + lr.height / 2;
        const lxs = [
          Math.round(lr.left + 4),
          Math.round(lr.left + lr.width / 2),
          Math.round(lr.right - 4),
        ];
        let allLight = true;
        let hadSample = false;
        for (const lx of lxs) {
          const { lum, isImage } = sampleAt(lx, ly);
          if (isImage) { allLight = false; hadSample = true; break; }
          if (lum == null) continue;
          hadSample = true;
          // Bias toward "stay white": only flip dark on a clearly light surface.
          if (lum < 180) { allLight = false; break; }
        }
        if (!hadSample) return;
        const nextLogoOverLight = allLight;
        if (nextLogoOverLight === logoOverLightRef.current) {
          pendingLogoOverLightRef.current = null;
          return;
        }
        if (pendingLogoOverLightRef.current === nextLogoOverLight) {
          logoOverLightRef.current = nextLogoOverLight;
          pendingLogoOverLightRef.current = null;
          setLogoOverLight(nextLogoOverLight);
        } else {
          pendingLogoOverLightRef.current = nextLogoOverLight;
        }
      };

      if (overlapsMarkedMediaHero(headerHeight)) {
        setOverImage(true);
        pendingOverDarkRef.current = null;
        overDarkRef.current = true;
        setOverDark(true);
        // Marked hero = dark photographic background → logo stays white.
        if (logoOverLightRef.current) {
          logoOverLightRef.current = false;
          pendingLogoOverLightRef.current = null;
          setLogoOverLight(false);
        }
        return;
      }

      sampleLogo();

      const w = window.innerWidth;
      const xs = [Math.round(w * 0.25), Math.round(w * 0.5), Math.round(w * 0.75)];
      const ys = [
        Math.max(Math.round(headerHeight * 0.35), 18),
        Math.max(Math.round(headerHeight * 0.72), 32),
      ];

      let darkVotes = 0;
      let lightVotes = 0;
      let imageVotes = 0;
      for (const sy of ys) {
        for (const sx of xs) {
          const { lum, isImage } = sampleAt(sx, sy);
          if (isImage) imageVotes++;
          if (lum == null) continue;
          if (lum < 128) darkVotes++;
          else lightVotes++;
        }
      }
      const nextOverImage = imageVotes >= 2;
      setOverImage(nextOverImage);
      if (darkVotes === 0 && lightVotes === 0) return;
      const nextOverDark = darkVotes >= lightVotes;

      // Hysteresis: only flip when two consecutive samples agree on the new value.
      if (nextOverDark === overDarkRef.current) {
        pendingOverDarkRef.current = null;
        return;
      }
      if (pendingOverDarkRef.current === nextOverDark) {
        overDarkRef.current = nextOverDark;
        pendingOverDarkRef.current = null;
        setOverDark(nextOverDark);
      } else {
        pendingOverDarkRef.current = nextOverDark;
      }
    };

    const mediaListeners = new WeakSet<EventTarget>();
    const mediaCleanupFns: Array<() => void> = [];
    const onMediaReady = () => {
      pendingOverDarkRef.current = null;
      const firstFrame = window.requestAnimationFrame(() => {
        detect();
        const secondFrame = window.requestAnimationFrame(detect);
        rafIds.push(secondFrame);
      });
      rafIds.push(firstFrame);
    };

    const bindMediaListeners = () => {
      document.querySelectorAll("img, video").forEach((el) => {
        if (mediaListeners.has(el)) return;
        mediaListeners.add(el);

        if (el instanceof HTMLImageElement) {
          const onLoad = () => onMediaReady();
          el.addEventListener("load", onLoad);
          mediaCleanupFns.push(() => el.removeEventListener("load", onLoad));
          return;
        }

        if (el instanceof HTMLVideoElement) {
          const onVideoReady = () => onMediaReady();
          el.addEventListener("loadeddata", onVideoReady);
          el.addEventListener("loadedmetadata", onVideoReady);
          el.addEventListener("canplay", onVideoReady);
          mediaCleanupFns.push(() => {
            el.removeEventListener("loadeddata", onVideoReady);
            el.removeEventListener("loadedmetadata", onVideoReady);
            el.removeEventListener("canplay", onVideoReady);
          });
        }
      });
    };

    const scrollContainers: HTMLElement[] = [];
    const listenerOptions = { passive: true };
    const rafIds: number[] = [];

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        detect();
        ticking = false;
      });
    };

    const bindScrollListeners = () => {
      document
        .querySelectorAll<HTMLElement>("main, [data-scroll-container]")
        .forEach((container) => {
          scrollContainers.push(container);
          container.addEventListener("scroll", onScroll, listenerOptions);
        });
    };

    const onResize = () => {
      pendingOverDarkRef.current = null;
      detect();
    };

    window.addEventListener("scroll", onScroll, listenerOptions);
    window.addEventListener("resize", onResize, listenerOptions);
    // Run detection across several frames after route change so newly-mounted
    // sections are measured before the user scrolls.
    const scheduleDetect = (delay: number) => {
      const id = window.setTimeout(() => {
        const r = window.requestAnimationFrame(() => {
          bindScrollListeners();
          bindMediaListeners();
          detect();
          // Force a second sample on next frame to settle hysteresis.
          const r2 = window.requestAnimationFrame(detect);
          rafIds.push(r2);
        });
        rafIds.push(r);
      }, delay);
      rafIds.push(id);
    };
    scheduleDetect(0);
    scheduleDetect(120);
    scheduleDetect(400);

    return () => {
      rafIds.forEach((id) => {
        window.cancelAnimationFrame(id);
        window.clearTimeout(id);
      });
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      scrollContainers.forEach((container) => container.removeEventListener("scroll", onScroll));
      mediaCleanupFns.forEach((cleanup) => cleanup());
    };
  }, [location.pathname, location.hash, menuOpen]);

  useEffect(() => {
    if (suppressNextLocationCloseRef.current && menuOpenRef.current) return;
    closeMenu();
  }, [closeMenu, location]);

  // Reset header chrome to "top of page" defaults on every route change so
  // returning to the homepage (or any route) does not inherit the previous
  // page's scrolled/hidden/dark state until the scroll listener catches up.
  useEffect(() => {
    setScrolled(false);
    setHidden(false);
    // Pages that begin with a uniformly light (stone) hero default the header
    // to "over light" so the logo renders dark immediately on mount instead of
    // briefly flashing white before the luminance sampler catches up.
    // Only the /work INDEX is a light stone layout. Project detail routes
    // (/work/:slug) open with a full-bleed dark hero, so they keep the
    // default `overDark=true` to render the logo white on first paint.
    const lightStartRoutes = ["/work"];
    const startsLight = lightStartRoutes.includes(location.pathname);
    setOverDark(!startsLight);
    overDarkRef.current = !startsLight;
    setOverImage(false);
    setLogoOverLight(startsLight);
    logoOverLightRef.current = startsLight;
    pendingLogoOverLightRef.current = null;
    pendingOverDarkRef.current = null;
    lastScrollY.current = 0;
  }, [location.pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  // On mobile (<md), submenus stay COLLAPSED by default. Users tap a primary
  // item to cascade open its sub-links (accordion behavior). Reset on close.
  useEffect(() => {
    if (!menuOpen) {
      setLabsHovered(false);
      setEcosystemHovered(false);
      setWorkHovered(false);
      setSolutionsHovered(false);
      setAboutHovered(false);
    }
  }, [menuOpen]);


  // When the menu is open and we're on a route whose section is scroll-spied,
  // auto-expand that section so the active sublink indicator is visible.
  useEffect(() => {
    if (!menuOpen) return;
    const matching = menuConfig.find((e) => e.href === location.pathname);
    if (matching && mobileOpenKey === null) {
      setMobileOpenKey(matching.key);
    }
  }, [menuOpen, location.pathname, mobileOpenKey]);

  // on the active page so the mobile submenu can highlight it. Re-attaches on
  // every route change, ignores query-string / external sublinks.
  useEffect(() => {
    setActiveSublinkHash(null);

    // Collect the unique hash anchors that belong to the CURRENT route.
    const matchingEntry = menuConfig.find((e) => e.href === location.pathname);
    const hashAnchors = (matchingEntry?.subLinks ?? [])
      .map((s) => s.anchor)
      .filter((a) => a.startsWith("#"));
    if (hashAnchors.length === 0) return;

    let observer: IntersectionObserver | null = null;
    let rafId = 0;

    const attach = () => {
      const sections = hashAnchors
        .map((a) => document.querySelector(a))
        .filter((el): el is Element => !!el);
      if (sections.length === 0) {
        rafId = window.requestAnimationFrame(attach);
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
          if (visible[0]) {
            setActiveSublinkHash(`#${visible[0].target.id}`);
          }
        },
        {
          rootMargin: "-45% 0px -45% 0px",
          threshold: [0, 0.25, 0.5, 0.75, 1],
        },
      );
      sections.forEach((s) => observer!.observe(s));
    };

    rafId = window.requestAnimationFrame(attach);
    return () => {
      window.cancelAnimationFrame(rafId);
      observer?.disconnect();
    };
  }, [location.pathname]);


  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 will-change-transform translate-y-0 ${
          scrolled && !menuOpen && !overImage
            ? overDark
              ? "bg-charcoal/95 md:bg-charcoal/80 backdrop-blur-xl"
              : "bg-white/95 md:bg-white/80 backdrop-blur-xl"
            : "bg-transparent"
        }`}
      >
        <div className="relative px-6 md:px-10 lg:px-14 flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="transition-opacity duration-300 hover:opacity-100"
            onClick={() => {
              window.dispatchEvent(new CustomEvent("reset-hero"));
              import("@/lib/smoothScroll").then((m) => m.scrollRootToTop());
            }}
          >
            <img
              ref={logoRef}
              src={logoWhite}
              alt="codelumen"
              width={176}
              height={28}
              className={`h-6 md:h-7 w-auto transition-all duration-500 ${
                needsInvert ? "invert" : ""
              } ${scrolled && !menuOpen ? "opacity-100" : "opacity-70 md:opacity-85"}`}
            />
          </Link>



          <button
            onClick={() => {
              dismissMenuLabel();
              if (menuOpen) {
                closeMenu();
              } else {
                setMenuOpen(true);
              }
            }}
            onPointerEnter={dismissMenuLabel}
            onPointerDown={dismissMenuLabel}
            onFocus={dismissMenuLabel}
            className="group/menu relative z-[111] flex flex-col items-center transition-opacity duration-300"
            aria-label="Toggle menu"
          >
            {/* Barcode icon → X crossfade */}
            <div className="peer/orb relative h-16 md:h-20 w-16 md:w-20 flex items-center justify-center">
              <motion.div
                className="absolute h-16 md:h-20 w-16 md:w-20 rounded-full"
                animate={{
                  opacity: menuOpen ? 0 : [0.92, 1, 0.92],
                  scale: menuOpen ? 0.7 : [1, 1.06, 1],
                  background: menuOpen
                    ? MENU_GLOW_CLOSE_GRADIENT
                    : overDark
                      ? MENU_GLOW_GRADIENTS_DARK
                      : MENU_GLOW_GRADIENTS_LIGHT,
                }}
                transition={menuOpen ? { duration: 0.35, ease: [0.22, 1, 0.36, 1] } : MENU_GLOW_PULSE_TRANSITION}
              />
              {/* Homepage narrative overlay — tints the single Header orb based on
                  the active homepage section. Renders only on `/`; interior pages
                  are unaffected. Sits above the base pulse and blends via screen. */}
              {homeOrbTheme && !menuOpen && (
                <motion.div
                  aria-hidden
                  className="pointer-events-none absolute h-16 md:h-20 w-16 md:w-20 rounded-full"
                  style={{ mixBlendMode: "screen" }}
                  animate={{
                    background: `radial-gradient(circle, rgba(${homeOrbTheme.color}, ${Math.min(1, 0.85 * homeOrbTheme.intensity)}) 0%, rgba(${homeOrbTheme.color}, ${0.35 * homeOrbTheme.intensity}) 30%, rgba(${homeOrbTheme.color}, ${0.12 * homeOrbTheme.intensity}) 55%, transparent 72%)`,
                    scale: reduceMotion
                      ? homeOrbTheme.radius
                      : homeOrbTheme.breathe
                        ? [homeOrbTheme.radius * 0.96, homeOrbTheme.radius * 1.06, homeOrbTheme.radius * 0.96]
                        : homeOrbTheme.radius,
                    opacity: reduceMotion
                      ? Math.min(1, homeOrbTheme.intensity * 0.9)
                      : homeOrbTheme.flicker
                        ? [homeOrbTheme.intensity * 0.9, homeOrbTheme.intensity * 0.72, homeOrbTheme.intensity * 0.9, homeOrbTheme.intensity * 0.82, homeOrbTheme.intensity * 0.9]
                        : homeOrbTheme.breathe
                          ? [homeOrbTheme.intensity * 0.85, homeOrbTheme.intensity, homeOrbTheme.intensity * 0.85]
                          : homeOrbTheme.intensity * 0.9,
                  }}
                  transition={
                    reduceMotion
                      ? { duration: 0 }
                      : {
                          background: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
                          scale: homeOrbTheme.breathe
                            ? { duration: 6.2, repeat: Infinity, ease: "easeInOut" }
                            : { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
                          opacity: homeOrbTheme.flicker
                            ? { duration: 3.6, repeat: Infinity, ease: "easeInOut" }
                            : homeOrbTheme.breathe
                              ? { duration: 5.6, repeat: Infinity, ease: "easeInOut" }
                              : { duration: 1.0, ease: [0.22, 1, 0.36, 1] },
                        }
                  }
                />
              )}
              {/* Hover intensifier ring removed — learned interaction, no hover cue needed */}
              {/* <span
                aria-hidden
                className={`pointer-events-none absolute h-12 md:h-14 w-12 md:w-14 rounded-full opacity-1 transition-opacity duration-300 ${
                  overDark
                    ? "shadow-[0_0_24px_6px_rgba(255,180,107,0.45)]"
                    : "shadow-[0_0_24px_6px_rgba(255,120,20,0.35)]"
                }`}
              /> */}
              <motion.div
                className="absolute flex items-center justify-center pointer-events-none"
                animate={{
                  opacity: menuOpen ? 1 : 0,
                  scale: menuOpen ? 1 : 0.7,
                  rotate: menuOpen ? 0 : -45,
                }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <motion.svg
                  width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round"
                  animate={{
                    stroke: KELVIN_STROKE_COLORS,
                  }}
                  transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                >
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </motion.svg>
              </motion.div>
            </div>
            {/* MENU caption underneath the orb · visible on fresh load, hides
                permanently after the user first hovers/taps the orb. */}
            <span
              className={`pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1 block font-sans font-light text-[9px] uppercase tracking-[0.32em] transition-opacity duration-300 ${
                menuOpen || menuLabelDismissed
                  ? "opacity-0"
                  : overDark
                    ? "text-white/70"
                    : "text-charcoal/55"
              }`}
            >
              Menu
            </span>
          </button>
        </div>
      </header>

      {/* Overlay + Sidebar */}
      <AnimatePresence>
        {menuOpen && (
          <SimplifiedMenuOverlay
            onClose={closeMenu}
            onNavIntent={playNavLogoSequence}
            onProjectReview={handleSimplifiedProjectReview}
            onRouteSelect={handleSimplifiedRoute}
            navLogoSequenceActive={navLogoSequenceActive}
            navLogoProgress={navLogoProgress}
            navLogoTriggerId={navLogoTriggerId}
          />
        )}
        {SHOW_LEGACY_MENU && menuOpen && (
          <>
            {/* Backdrop blur overlay, click to close */}
            <motion.div
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="fixed inset-0 z-[100] bg-[hsl(var(--charcoal)/0.7)] backdrop-blur-xl"
              onClick={closeMenu}
            />

            {/* Sidebar panel */}
            <motion.aside
              key="sidebar"
              variants={sidebarVariants}
              initial="closed"
              animate="open"
              exit="exit"
              className="fixed top-0 right-0 bottom-0 z-[110] w-full md:left-0 md:w-full bg-[hsl(var(--charcoal))] flex flex-col"
            >
              {/* Top bar: logo centered, close button right */}
              <div className="px-8 md:px-12 flex items-center justify-center relative h-16 md:h-20 flex-shrink-0">
                {/* Four-layer menu mark: dim at rest, subtle on sublink hover,
                    explicit 1→2→3→4 sequence only on navigation taps. */}
                {(() => {
                  const activeBar: number | "all" | null =
                    typeof hoveredSubBar === "number"
                      ? hoveredSubBar % 4
                      : hoveredSubBar;
                  return (
                    <>
                      <LogoBars
                        key={`nav-logo-desktop-${navLogoTriggerId}`}
                        variant={
                          navLogoSequenceActive
                            ? "progress"
                            : activeBar !== null
                              ? "interactive"
                              : "idle"
                        }
                        progress={navLogoProgress}
                        activeBar={activeBar}
                        tone="light"
                        size="md"
                        ariaLabel="codelumen — four-layer system"
                        className="hidden md:inline-flex"
                      />
                      <LogoBars
                        key={`nav-logo-mobile-${navLogoTriggerId}`}
                        variant="sequence"
                        triggerKey={navLogoTriggerId}
                        tone="light"
                        size="sm"
                        ariaLabel="codelumen — four-layer system"
                        className="inline-flex md:hidden"
                      />
                    </>
                  );
                })()}
                <button
                  onClick={closeMenu}
                  aria-label="Close menu"
                  className="absolute right-8 md:right-12 text-white/70 transition-all duration-500 hover:text-white hover:drop-shadow-[0_0_18px_rgba(255,255,255,0.35)]"
                >

                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="18" y1="6" x2="6" y2="18" />
                  </svg>
                </button>
              </div>

              {/* Primary links — MOBILE accordion (<md). Desktop uses the editorial mega-menu below. */}
              <motion.nav
                ref={mobileNavRef as never}
                variants={linkContainerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="md:hidden relative flex-1 overflow-y-auto flex flex-col px-8 py-8 snap-y snap-proximity overscroll-contain"
              >
                {/* Ambient slow-moving light gradient, premium feel, never distracting */}
                <motion.div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -z-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1.2, ease: "easeOut" }}
                  style={{
                    background:
                      "radial-gradient(ellipse 60% 40% at 80% 10%, rgba(255,255,255,0.05) 0%, transparent 60%), radial-gradient(ellipse 50% 35% at 10% 90%, rgba(255,255,255,0.04) 0%, transparent 60%)",
                  }}
                >
                  <motion.div
                    className="absolute inset-0"
                    animate={{ opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                      background:
                        "radial-gradient(ellipse 40% 30% at 70% 30%, rgba(255,255,255,0.035) 0%, transparent 70%)",
                    }}
                  />
                </motion.div>

                {/* Dynamic ecosystem phrase, rotates each menu open */}
                <div className="snap-start relative max-w-[380px] md:max-w-2xl mb-6 md:mb-12">
                  {/* Rotating index eyebrow */}
                  <motion.p
                    key={`idx-${phraseIndex}`}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    className="font-sans font-light text-white/40 text-[10px] tracking-[0.25em] uppercase mb-2"
                  >
                    {String(phraseIndex + 1).padStart(2, "0")} · {String(ecosystemPhrases.length).padStart(2, "0")}
                  </motion.p>

                  {/* Phrase, character cascade with previous-line ghost */}
                  <p
                    aria-label={activePhrase}
                    className="relative font-sans font-light text-white text-xs md:text-base leading-relaxed tracking-[0.15em] uppercase"
                  >
                    {/* Ghost of previous phrase, fades out as new one writes in */}
                    <motion.span
                      key={`ghost-${phraseIndex}`}
                      aria-hidden
                      initial={{ opacity: 0.2, filter: "blur(2px)" }}
                      animate={{ opacity: 0, filter: "blur(8px)" }}
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0 pointer-events-none"
                    >
                      {prevPhraseRef.current}
                    </motion.span>

                    {/* New phrase, character by character, words kept intact */}
                    <span aria-hidden className="relative">
                      {(() => {
                        const tokens = activePhrase.split(/(\s+)/);
                        let ci = 0;
                        return tokens.map((token, ti) => {
                          if (/^\s+$/.test(token)) {
                            ci += token.length;
                            return (
                              <span key={`sp-${ti}`} className="whitespace-pre">
                                {token}
                              </span>
                            );
                          }
                          return (
                            <span key={`w-${ti}`} className="inline-block whitespace-nowrap align-baseline">
                              {Array.from(token).map((ch) => {
                                const idx = ci++;
                                return (
                                  <motion.span
                                    key={`${activePhrase}-${idx}`}
                                    initial={{ opacity: 0, y: 4, filter: "blur(3px)" }}
                                    animate={{ opacity: 0.85, y: 0, filter: "blur(0px)" }}
                                    transition={{
                                      duration: 0.4,
                                      delay: 0.35 + idx * 0.012,
                                      ease: [0.22, 1, 0.36, 1],
                                    }}
                                    className="inline-block"
                                  >
                                    {ch}
                                  </motion.span>
                                );
                              })}
                            </span>
                          );
                        });
                      })()}
                    </span>
                  </p>

                  {/* Hairline that draws beneath, signals "complete" */}
                  <motion.span
                    key={`rule-${phraseIndex}`}
                    aria-hidden
                    initial={{ scaleX: 0, opacity: 0 }}
                    animate={{ scaleX: 1, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="block mt-2 h-px w-full bg-white/30 origin-left"
                  />
                </div>

                {/*
                  Mobile menu cascade (single-open accordion).
                  Sourced 1:1 from `menuConfig` so desktop mega-menu and mobile
                  cascade can never drift. Tap a row label to expand/collapse;
                  navigation only happens via the per-section CTA inside the
                  expanded group, or by tapping a sublink.
                */}
                <div className="relative space-y-1">
                  {(() => {
                    // Resolve a sublink's destination path for in-app navigation.
                    // Supports hash anchors (`#design`), query strings (`?service=...`),
                    // absolute paths (`/contact`), and mailto: / external URLs.
                    const resolveSublinkHref = (basePath: string, anchor: string) => {
                      if (anchor.startsWith("http") || anchor.startsWith("mailto:")) return anchor;
                      if (anchor.startsWith("/")) return anchor;
                      return `${basePath}${anchor}`;
                    };

                    // Smooth scroll-or-navigate handler used for hash sublinks
                    // (so /system#design behaves the same as the desktop menu).
                    const handleSublinkNavigate = (basePath: string, anchor: string) => {
                      const href = resolveSublinkHref(basePath, anchor);

                      // External / mailto: let the browser handle it natively.
                      if (href.startsWith("http") || href.startsWith("mailto:")) {
                        closeMenu();
                        document.body.style.overflow = "";
                        window.location.href = href;
                        return;
                      }

                      // Helper: smooth-scroll to a hash anchor with the
                      // 88px sticky-header offset, polling briefly so it
                      // also works after a route change when the target
                      // section mounts on the next paint.
                      const scrollToAnchor = (hash: string) => {
                        const tryScroll = (attempt: number) => {
                          const el = document.querySelector(hash);
                          if (el) {
                            import("@/lib/smoothScroll").then((m) =>
                              m.scrollToHash(hash),
                            );
                            return;
                          }
                          if (attempt < 20) {
                            window.setTimeout(() => tryScroll(attempt + 1), 60);
                          }
                        };
                        requestAnimationFrame(() => tryScroll(0));
                      };

                      // Query-string sublinks (e.g. /work?service=...): just
                      // navigate; the destination page handles the filter +
                      // its own scroll positioning.
                      if (anchor.startsWith("?")) {
                        holdMenuForNavLogo(() => requestAnimationFrame(() => {
                          if (location.pathname + location.search === href) {
                            import("@/lib/smoothScroll").then((m) => m.scrollRootToTop());
                          } else {
                            navigate(href);
                            requestAnimationFrame(() =>
                              import("@/lib/smoothScroll").then((m) => m.scrollRootToTop()),
                            );
                          }
                        }));
                        return;
                      }

                      // Absolute path sublinks (e.g. /contact) with optional hash.
                      if (anchor.startsWith("/")) {
                        const [path, hash] = href.split("#");
                        const samePage = location.pathname === path;
                        holdMenuForNavLogo(() => requestAnimationFrame(() => {
                          if (samePage) {
                            if (hash) scrollToAnchor(`#${hash}`);
                            else import("@/lib/smoothScroll").then((m) => m.scrollRootToTop());
                          } else {
                            navigate(href);
                            if (hash) {
                              scrollToAnchor(`#${hash}`);
                            } else {
                              requestAnimationFrame(() =>
                                import("@/lib/smoothScroll").then((m) => m.scrollRootToTop()),
                              );
                            }
                          }
                        }));
                        return;
                      }

                      // Hash anchor relative to the current section's basePath.
                      if (anchor.startsWith("#")) {
                        const samePage = location.pathname === basePath;
                        holdMenuForNavLogo(() => requestAnimationFrame(() => {
                          if (samePage) {
                            scrollToAnchor(anchor);
                          } else {
                            navigate(href);
                            scrollToAnchor(anchor);
                          }
                        }));
                        return;
                      }

                      // Fallback: plain navigate.
                      holdMenuForNavLogo(() => requestAnimationFrame(() => navigate(href)));
                    };

                    /**
                     * Mobile menu LABEL tap handler.
                     *
                     * Order of operations matters here so the destination page
                     * is fully visible the moment the menu fades:
                     *
                     *   1. Release the body scroll lock immediately so the
                     *      destination is scrollable from frame 1.
                     *   2. Navigate (or scroll-to-top on same-page taps) FIRST
                     *      so the new content is mounted underneath the still-
                     *      fading overlay rather than after it.
                     *   3. Then collapse the menu — its 300ms exit animation
                     *      now reveals the new page instead of blocking it.
                     *   4. Smooth-scroll to top on the next paint so the
                     *      destination glides into view from its top edge.
                     */
                    const handleLabelNavigate = (targetHref: string) => {
                      const samePage = location.pathname === targetHref;

                      holdMenuForNavLogo(() => requestAnimationFrame(() => {
                        if (samePage) {
                          import("@/lib/smoothScroll").then((m) => m.scrollRootToTop());
                        } else {
                          navigate(targetHref);
                          requestAnimationFrame(() => {
                            import("@/lib/smoothScroll").then((m) => m.scrollRootToTop());
                          });
                        }
                      }));
                    };
                    return menuConfig.map((entry) => {
                      const basePath = entry.href;
                      const isOpen = mobileOpenKey === entry.key;
                      const isActive =
                        basePath === "/"
                          ? location.pathname === "/"
                          : location.pathname === basePath ||
                            location.pathname.startsWith(basePath + "/");

                      const toggle = () =>
                        setMobileOpenKey((prev) => (prev === entry.key ? null : entry.key));

                      return (
                        <motion.div
                          key={entry.key}
                          ref={(el) => {
                            mobileEntryRefs.current[entry.key] = el as HTMLDivElement | null;
                          }}
                          variants={linkItemVariants}
                          className="snap-start border-b border-white/10 last:border-b-0"
                        >
                          {/*
                            Mobile: tapping the row expands the accordion in
                            place so users can see the sublinks under the
                            title. To reach the page itself, users tap the
                            per-section CTA rendered inside the expanded panel
                            (canonical label from menuConfig).
                          */}
                          <button
                            type="button"
                            onClick={() => {
                              playNavLogoSequence();
                              toggle();
                            }}
                            aria-expanded={isOpen}
                            aria-controls={`mobile-section-${entry.key}`}
                            aria-label={`${isOpen ? "Collapse" : "Expand"} ${entry.label} submenu`}
                            className="group flex w-full items-center justify-between py-3 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-white/40"
                          >
                            <motion.span
                              className={`font-sans font-light text-base sm:text-lg leading-[1.1] tracking-[-0.02em] inline-block transition-all duration-500 group-hover:text-white ${
                                isActive || isOpen ? "text-white" : "text-white/70"
                              }`}
                            >
                              {entry.label}
                            </motion.span>
                            <ChevronDown
                              size={14}
                              strokeWidth={1.25}
                              className={`shrink-0 ml-3 text-white/40 group-hover:text-white/70 transition-all duration-300 ease-out ${
                                isOpen ? "rotate-180 text-white/70" : ""
                              }`}
                            />
                          </button>


                          {/* Expanded group: eyebrow → divided sublinks → per-section CTA */}
                          <AnimatePresence initial={false}>
                            {isOpen && (
                              <motion.div
                                id={`mobile-section-${entry.key}`}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                className="overflow-hidden"
                              >
                                <div className="pt-2 pb-5">
                                  {/* Eyebrow — matches desktop framing */}
                                  <p className="font-sans font-light text-white/40 text-[10px] tracking-[0.25em] uppercase mb-3">
                                    {entry.eyebrow}
                                  </p>

                                  {/* Sublinks with hairline dividers */}
                                  <ul className="border-t border-white/10">
                                    {entry.subLinks.map((sub) => {
                                      const isActiveSub =
                                        location.pathname === basePath &&
                                        sub.anchor.startsWith("#") &&
                                        activeSublinkHash === sub.anchor;
                                      return (
                                        <li key={sub.label} className="border-b border-white/10">
                                          <a
                                            href={resolveSublinkHref(basePath, sub.anchor)}
                                            onClick={(e) => {
                                              if (
                                                sub.anchor.startsWith("mailto:") ||
                                                sub.anchor.startsWith("http")
                                              ) {
                                                closeMenu();
                                                return;
                                              }
                                              e.preventDefault();
                                              handleSublinkNavigate(basePath, sub.anchor);
                                            }}

                                            aria-current={isActiveSub ? "location" : undefined}
                                            className="group/sub relative flex items-center justify-between py-3 pl-4"
                                          >
                                            {/* Active indicator: thin vertical bar fades/scales in */}
                                            <motion.span
                                              aria-hidden="true"
                                              initial={false}
                                              animate={{
                                                opacity: isActiveSub ? 1 : 0,
                                                scaleY: isActiveSub ? 1 : 0.4,
                                              }}
                                              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                              className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[2px] bg-white origin-center"
                                            />
                                            <span
                                              className={`font-sans font-light text-sm tracking-[0.04em] transition-colors duration-300 group-hover/sub:text-white ${
                                                isActiveSub ? "text-white" : "text-white/70"
                                              }`}
                                            >
                                              {sub.label}
                                            </span>
                                          </a>
                                        </li>
                                      );
                                    })}
                                  </ul>

                                  {/* Per-section CTA — canonical label from menuConfig */}
                                  <div className="mt-5">
                                    <Link
                                      to={entry.cta.href}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        holdMenuForNavLogo(() => navigate(entry.cta.href));
                                      }}

                                      className="group inline-flex items-center gap-3 px-5 py-2.5 border border-white/40 hover:border-white transition-colors duration-300"
                                    >
                                      <span className="font-sans font-light text-white text-xs tracking-[0.08em] uppercase">
                                        {entry.cta.label}
                                      </span>
                                      <BtnArrow className="text-white" />
                                    </Link>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    });
                  })()}

                  {/* Contact is already rendered via menuConfig above. */}
                </div>

                {/* Micro value statement, sits between primary nav and CTA */}
                <motion.p
                  variants={linkItemVariants}
                  className="relative mt-6 md:mt-8 font-sans font-light text-white/45 text-xs md:text-sm tracking-[0.04em] leading-relaxed max-w-[420px]"
                >
                  One system. One accountable team. Measurable outcomes.
                </motion.p>

                {/* Primary CTA · Request a Project Review (opens unified intake modal) */}
                <motion.div variants={linkItemVariants} className="relative mt-5 md:mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      holdMenuForNavLogo(() => {
                        window.setTimeout(() => {
                          window.dispatchEvent(
                            new CustomEvent("open-consultation", {
                              detail: { intent: "consultation" },
                            }),
                          );
                        }, NAV_LOGO_VISIBLE_CLOSE_DELAY_MS);
                      });
                    }}
                    className="group/cta relative inline-flex items-center gap-3 px-6 py-3 md:px-7 md:py-3.5 border border-white/40 hover:border-white transition-all duration-500"
                  >
                    {/* Subtle ambient glow on hover */}
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0 opacity-0 group-hover/cta:opacity-100 transition-opacity duration-700"
                      style={{
                        background:
                          "radial-gradient(ellipse 80% 120% at 50% 50%, rgba(255,255,255,0.12) 0%, transparent 70%)",
                        boxShadow: "0 0 32px rgba(255,255,255,0.15)",
                      }}
                    />
                    <span className="relative font-sans font-light text-white text-sm md:text-base tracking-[0.08em] uppercase">
                      Request a Project Review
                    </span>
                    <motion.span
                      className="relative text-white"
                      initial={false}
                      whileHover={{ x: 4 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <svg width="16" height="10" viewBox="0 0 16 10" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round">
                        <line x1="0" y1="5" x2="14" y2="5" />
                        <polyline points="10,1 14,5 10,9" />
                      </svg>
                    </motion.span>
                  </button>
                </motion.div>

                {/* Divider */}
                <motion.div
                  variants={linkItemVariants}
                  className="relative my-6 md:my-8 h-px bg-white/20"
                />

                {/* How We Deliver, reinforces 4-layer system — hidden on mobile (already covered by per-item submenus) */}
                <div className="relative hidden md:block">
                  <motion.p
                    variants={linkItemVariants}
                    className="font-sans font-light text-white/40 text-[10px] md:text-xs tracking-[0.25em] uppercase mb-4"
                  >
                    How we deliver
                  </motion.p>
                  <div className="space-y-2 md:space-y-2.5">
                    {secondaryLinks.map((link) => (
                      <motion.div key={link.label} variants={linkItemVariants}>
                        <Link
                          to={link.href}
                          onClick={(e) => {
                            e.preventDefault();
                            holdMenuForNavLogo(() => navigate(link.href));
                          }}
                          onPointerEnter={() => {
                            const map: Record<string, number> = {
                              "Lighting Design": 0,
                              "Product Engineering": 1,
                              "Controls Integration": 2,
                              "Execution Platform": 3,
                            };
                            if (link.label in map) setHoveredSubBar(map[link.label]);
                          }}
                          onPointerLeave={() => setHoveredSubBar(null)}
                          className="group/sec inline-block"
                        >
                          <span className="relative font-sans font-light text-white/65 text-sm md:text-base tracking-[0.04em] inline-block transition-all duration-500 group-hover/sec:text-white group-hover/sec:drop-shadow-[0_0_14px_rgba(255,255,255,0.3)]">
                            {link.label}
                            <span className="absolute left-0 -bottom-0.5 h-px w-full origin-left scale-x-0 bg-white/60 transition-transform duration-500 ease-out group-hover/sec:scale-x-100" />
                          </span>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Divider — hidden on mobile alongside the How We Deliver block above */}
                <motion.div
                  variants={linkItemVariants}
                  className="relative my-6 md:my-8 h-px bg-white/20 hidden md:block"
                />


                {/* Divider */}
                <motion.div
                  variants={linkItemVariants}
                  className="relative my-6 md:my-8 h-px bg-white/20"
                />

                {/* Social caption + icons + tagline + copyright */}
                <div className="relative flex flex-col gap-4">
                  <motion.p
                    variants={linkItemVariants}
                    className="font-sans font-light text-white/45 text-xs md:text-sm tracking-[0.04em] leading-relaxed max-w-[380px]"
                  >
                    Follow real project execution, not just renderings.
                  </motion.p>
                  <motion.div variants={linkItemVariants} className="flex items-center gap-5">
                    <a
                      href="https://www.linkedin.com/company/clumen/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/50 transition-all duration-500 hover:text-white hover:drop-shadow-[0_0_14px_rgba(255,255,255,0.3)]"
                      aria-label="LinkedIn"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                    <a
                      href="https://www.instagram.com/codelumen"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/50 transition-all duration-500 hover:text-white hover:drop-shadow-[0_0_14px_rgba(255,255,255,0.3)]"
                      aria-label="Instagram"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                      </svg>
                    </a>
                  </motion.div>
                  <motion.p
                    variants={linkItemVariants}
                    className="font-sans font-light text-white/40 text-xs tracking-[0.15em] italic"
                  >
                    Light Better. Live Better.
                  </motion.p>
                  <motion.p
                    variants={linkItemVariants}
                    className="font-sans text-white/20 text-[10px] tracking-[0.2em]"
                  >
                    © 2026 codelumen · All rights reserved.
                  </motion.p>
                </div>
              </motion.nav>

              {/* Desktop / iPad editorial mega-menu (≥md) */}
              <motion.div
                variants={linkContainerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="hidden md:flex relative flex-1 min-h-0 flex-col"
                onMouseLeave={() => {
                  if (hoverGraceRef.current) window.clearTimeout(hoverGraceRef.current);
                  hoverGraceRef.current = window.setTimeout(() => {
                    setActiveMenuKey("system");
                    setHoveredPrimaryIndex(null);
                  }, 180);
                }}
                onMouseEnter={() => {
                  if (hoverGraceRef.current) {
                    window.clearTimeout(hoverGraceRef.current);
                    hoverGraceRef.current = null;
                  }
                }}
              >
                {/* Ambient gradient backdrop */}
                <motion.div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -z-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1.2, ease: "easeOut" }}
                  style={{
                    background:
                      "radial-gradient(ellipse 60% 40% at 80% 10%, rgba(255,255,255,0.05) 0%, transparent 60%), radial-gradient(ellipse 50% 35% at 10% 90%, rgba(255,255,255,0.04) 0%, transparent 60%)",
                  }}
                />

                {/* Quote rail (top, full width above grid) */}
                <motion.div variants={linkItemVariants} className="relative px-12 lg:px-20 xl:px-28 pt-10 lg:pt-14 max-w-5xl">
                  <motion.p
                    key={`d-idx-${phraseIndex}`}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    className="font-sans font-light text-white/40 text-[10px] tracking-[0.25em] uppercase mb-3"
                  >
                    {String(phraseIndex + 1).padStart(2, "0")} · {String(ecosystemPhrases.length).padStart(2, "0")}
                  </motion.p>
                  <p
                    aria-label={activePhrase}
                    className="relative font-sans font-light text-white text-sm lg:text-base leading-relaxed tracking-[0.15em] uppercase max-w-3xl"
                  >
                    <motion.span
                      key={`d-ghost-${phraseIndex}`}
                      aria-hidden
                      initial={{ opacity: 0.2, filter: "blur(2px)" }}
                      animate={{ opacity: 0, filter: "blur(8px)" }}
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0 pointer-events-none"
                    >
                      {prevPhraseRef.current}
                    </motion.span>
                    <span aria-hidden className="relative">
                      {(() => {
                        const tokens = activePhrase.split(/(\s+)/);
                        let ci = 0;
                        return tokens.map((token, ti) => {
                          if (/^\s+$/.test(token)) {
                            ci += token.length;
                            return (
                              <span key={`d-sp-${ti}`} className="whitespace-pre">
                                {token}
                              </span>
                            );
                          }
                          return (
                            <span key={`d-w-${ti}`} className="inline-block whitespace-nowrap align-baseline">
                              {Array.from(token).map((ch) => {
                                const idx = ci++;
                                return (
                                  <motion.span
                                    key={`d-${activePhrase}-${idx}`}
                                    initial={{ opacity: 0, y: 4, filter: "blur(3px)" }}
                                    animate={{ opacity: 0.85, y: 0, filter: "blur(0px)" }}
                                    transition={{
                                      duration: 0.4,
                                      delay: 0.35 + idx * 0.012,
                                      ease: [0.22, 1, 0.36, 1],
                                    }}
                                    className="inline-block"
                                  >
                                    {ch}
                                  </motion.span>
                                );
                              })}
                            </span>
                          );
                        });
                      })()}
                    </span>
                  </p>
                  <motion.span
                    key={`d-rule-${phraseIndex}`}
                    aria-hidden
                    initial={{ scaleX: 0, opacity: 0 }}
                    animate={{ scaleX: 1, opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="block mt-3 h-px w-32 bg-white/30 origin-left"
                  />
                </motion.div>

                {/* Two-column grid */}
                <div className="relative flex-1 min-h-0 grid grid-cols-12 gap-x-10 lg:gap-x-16 px-12 lg:px-20 xl:px-28 pt-10 pb-8 overflow-y-auto">
                  {/* LEFT — primary index + contextual meta */}
                  <nav className="col-span-5 lg:col-span-4 flex flex-col">
                    <div className="flex flex-col gap-1 lg:gap-1.5">
                    {menuConfig.map((entry, i) => {
                      const isActive =
                        location.pathname === entry.href ||
                        (entry.href !== "/" && location.pathname.startsWith(entry.href + "/"));
                      const isHot = hoveredPrimaryIndex !== null && activeMenuKey === entry.key;
                      const isFocused = isHot || (hoveredPrimaryIndex === null && isActive);
                      return (
                        <motion.div key={entry.key} variants={linkItemVariants}>
                          <Link
                            to={entry.href}
                            onClick={(e) => {
                              e.preventDefault();
                              const target = entry.href;
                              const samePage = location.pathname === target;
                              holdMenuForNavLogo(() => requestAnimationFrame(() => {
                                if (samePage) {
                                  import("@/lib/smoothScroll").then((m) => m.scrollRootToTop());
                                } else {
                                  navigate(target);
                                }
                              }));
                            }}
                            onMouseEnter={() => {
                              if (hoverGraceRef.current) {
                                window.clearTimeout(hoverGraceRef.current);
                                hoverGraceRef.current = null;
                              }
                              playNavLogoSequence();
                              setActiveMenuKey(entry.key);
                              setHoveredPrimaryIndex(i);
                            }}
                            onFocus={() => {
                              playNavLogoSequence();
                              setActiveMenuKey(entry.key);
                              setHoveredPrimaryIndex(i);
                            }}
                            className="group relative flex items-center py-2.5 lg:py-3 pl-5"
                          >
                            {/* Active / focused indicator — thin illuminated line */}
                            <motion.span
                              aria-hidden
                              initial={false}
                              animate={{
                                opacity: isFocused ? 1 : isActive ? 0.5 : 0,
                                scaleY: isFocused ? 1 : 0.6,
                              }}
                              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                              className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-px bg-white origin-center"
                              style={{
                                boxShadow: isFocused ? "0 0 10px rgba(255,255,255,0.7)" : "none",
                              }}
                            />
                            <span
                              className={`cl-h2 transition-all duration-500 ${
                                isFocused
                                  ? "text-white opacity-100"
                                  : isActive
                                    ? "text-white/80"
                                    : "text-white/50 group-hover:text-white"
                              }`}
                              style={{ fontSize: "clamp(1.125rem, 1.5vw, 1.5rem)" }}
                            >
                              {entry.label}
                            </span>
                          </Link>
                        </motion.div>
                      );
                    })}
                    </div>

                    {/* Contextual meta for the currently focused page */}
                    <div className="mt-auto pt-10 pl-5">
                      <AnimatePresence mode="wait">
                        {(() => {
                          const entry =
                            menuConfig.find((m) => m.key === activeMenuKey) ?? menuConfig[0];
                          const chapters = entry.subLinks.length;
                          const readMin = Math.max(3, Math.round(chapters * 1.1));
                          return (
                            <motion.div
                              key={`meta-${entry.key}`}
                              initial={{ opacity: 0, y: 6 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                              className="max-w-[22rem]"
                            >
                              <p className="cl-eyebrow text-white/35 mb-3">
                                Currently reading
                              </p>
                              <p className="font-sans font-light text-white/75 text-sm lg:text-[15px] leading-relaxed">
                                {entry.intro}
                              </p>
                              <p className="mt-4 font-sans font-light text-white/40 text-[11px] tracking-[0.2em] uppercase tabular-nums">
                                {chapters} Chapters · Approximately {readMin} minutes
                              </p>
                            </motion.div>
                          );
                        })()}
                      </AnimatePresence>
                    </div>
                  </nav>

                  {/* RIGHT — detail panel */}
                  <div className="col-span-7 lg:col-span-8 border-l border-white/10 pl-10 lg:pl-16 min-h-[320px]">
                    <AnimatePresence mode="wait">
                      {hoveredPrimaryIndex === null ? (
                        <motion.div
                          key="home"
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                          className="flex flex-col gap-14"
                        >
                          {/* Closing phrase */}
                          <div>
                            <p className="cl-h3 text-white max-w-xl">
                              Lighting isn't a finish.
                              <span className="block text-white/55">
                                It's the system that decides whether a project lands on intent, on budget, on schedule.
                              </span>
                            </p>
                          </div>

                          {/* Where to next — quiet inline row, no card */}
                          <div className="flex items-center gap-6 flex-wrap">
                            <span className="cl-eyebrow text-white/35">
                              Where to next
                            </span>
                            <span aria-hidden className="h-px w-8 bg-white/15" />
                            <button
                              type="button"
                              onMouseEnter={() => playNavLogoSequence()}
                              onFocus={() => playNavLogoSequence()}
                              onClick={() => {
                                holdMenuForNavLogo(() => {
                                  window.setTimeout(() => {
                                    window.dispatchEvent(
                                      new CustomEvent("open-consultation", {
                                        detail: { variant: "consultation" },
                                      }),
                                    );
                                  }, NAV_LOGO_VISIBLE_CLOSE_DELAY_MS);
                                });
                              }}
                              className="group/cta inline-flex items-center gap-2 font-sans font-light text-white/75 hover:text-white text-xs lg:text-[13px] tracking-[0.14em] uppercase transition-colors duration-500"
                            >
                              Request a Project Review
                              <span aria-hidden className="text-white/40 group-hover/cta:text-white transition-colors">→</span>
                            </button>
                          </div>
                        </motion.div>
                      ) : (
                        (() => {
                          const entry = menuConfig.find((m) => m.key === activeMenuKey) ?? menuConfig[0];
                          const chapters = entry.subLinks.length;
                          const currentChapter =
                            typeof hoveredSubBar === "number" ? hoveredSubBar + 1 : null;
                          return (
                            <motion.div
                              key={entry.key}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                            >
                              {/* Editorial header: eyebrow + stronger page title */}
                              <div className="flex items-baseline justify-between gap-6 mb-6">
                                <p className="cl-eyebrow text-white/45">
                                  {entry.eyebrow}
                                </p>
                                <p className="font-sans font-light text-white/40 text-[11px] tracking-[0.25em] uppercase tabular-nums">
                                  {currentChapter
                                    ? `Chapter ${String(currentChapter).padStart(2, "0")} of ${String(chapters).padStart(2, "0")}`
                                    : `${String(chapters).padStart(2, "0")} Chapters`}
                                </p>
                              </div>

                              <h2
                                className="font-sans font-light text-white tracking-[-0.02em] leading-[1.05] mb-6"
                                style={{ fontSize: "clamp(1.75rem, 2.6vw, 2.5rem)" }}
                              >
                                {entry.label}
                              </h2>

                              {entry.intro && (
                                <p className="font-sans font-light text-white/55 text-sm lg:text-[15px] leading-relaxed max-w-xl mb-10">
                                  {entry.intro}
                                </p>
                              )}

                              <div className="flex flex-col">
                                <ul className="divide-y divide-white/10 border-y border-white/10">
                                  {entry.subLinks.map((sub, si) => {
                                    const anchor = sub.anchor;
                                    const isMail = anchor.startsWith("mailto:");
                                    const isAbsolutePath = anchor.startsWith("/");
                                    const isQuery = anchor.startsWith("?");
                                    const isHash = anchor.startsWith("#");
                                    const href = isMail
                                      ? anchor
                                      : isAbsolutePath
                                        ? anchor
                                        : `${entry.href}${anchor}`;

                                    const handleSubClick = (e: React.MouseEvent) => {
                                      if (isMail) return;
                                      e.preventDefault();
                                      holdMenuForNavLogo(() => {
                                        if (isHash && location.pathname === entry.href) {
                                          import("@/lib/smoothScroll").then((m) =>
                                            m.scrollToHash(anchor),
                                          );
                                        } else if (isQuery) {
                                          navigate(`${entry.href}${anchor}`);
                                        } else if (isAbsolutePath) {
                                          navigate(anchor);
                                        } else {
                                          navigate(`${entry.href}${anchor}`);
                                        }
                                      });
                                    };

                                    return (
                                      <motion.li
                                        key={sub.label}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                          duration: 0.5,
                                          delay: 0.08 + si * 0.06,
                                          ease: [0.22, 1, 0.36, 1],
                                        }}
                                      >
                                        <a
                                          href={href}
                                          onClick={handleSubClick}
                                          onPointerEnter={() => setHoveredSubBar(si)}
                                          onPointerLeave={() => setHoveredSubBar(null)}
                                          onFocus={() => setHoveredSubBar(si)}
                                          onBlur={() => setHoveredSubBar(null)}
                                          className="group/step grid grid-cols-[auto_1fr] gap-x-6 lg:gap-x-8 items-baseline py-4 lg:py-[1.15rem]"
                                        >
                                          <span className="font-sans font-light text-white/30 text-[11px] tracking-[0.25em] tabular-nums transition-colors duration-500 group-hover/step:text-white/70">
                                            {String(si + 1).padStart(2, "0")}
                                          </span>
                                          <span className="min-w-0">
                                            <span className="block font-sans font-light text-white text-xl lg:text-2xl tracking-[-0.01em] leading-tight transition-all duration-500 group-hover/step:translate-x-1 group-hover/step:drop-shadow-[0_0_14px_rgba(255,255,255,0.3)]">
                                              {sub.label}
                                            </span>
                                            {sub.description && (
                                              <span className="mt-2 block font-sans font-light text-white/45 text-xs lg:text-[13px] leading-relaxed max-w-md">
                                                {sub.description}
                                              </span>
                                            )}
                                          </span>
                                        </a>
                                      </motion.li>
                                    );
                                  })}
                                </ul>
                                <motion.div
                                  initial={{ opacity: 0, y: 8 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{
                                    duration: 0.5,
                                    delay: 0.08 + entry.subLinks.length * 0.06,
                                    ease: [0.22, 1, 0.36, 1],
                                  }}
                                  className="mt-6"
                                >
                                  <Link
                                    to={entry.cta.href}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const target = entry.cta.href;
                                      const [path, hash] = target.split("#");
                                      const samePage = location.pathname === path;
                                      holdMenuForNavLogo(() => requestAnimationFrame(() => {
                                        if (samePage) {
                                          if (hash) {
                                            import("@/lib/smoothScroll").then((m) => m.scrollToHash(`#${hash}`));
                                          } else {
                                            import("@/lib/smoothScroll").then((m) => m.scrollRootToTop());
                                          }
                                        } else {
                                          navigate(target);
                                        }
                                      }));
                                    }}
                                    className="group/cta inline-flex items-center gap-2 font-sans font-light text-white/70 hover:text-white text-xs lg:text-[13px] tracking-[0.12em] uppercase transition-colors duration-500"
                                  >
                                    {entry.cta.label}
                                    <span aria-hidden className="text-white/40 group-hover/cta:text-white transition-colors">→</span>
                                  </Link>
                                </motion.div>
                              </div>
                            </motion.div>
                          );
                        })()
                      )}
                    </AnimatePresence>
                  </div>
                </div>


                {/* Footer rail */}
                <motion.div
                  variants={linkItemVariants}
                  className="relative border-t border-white/10 px-12 lg:px-20 xl:px-28 py-5 flex items-center justify-between gap-8 flex-wrap"
                >
                  <div className="flex items-center gap-8 flex-wrap" />
                  <div className="flex items-center gap-6">
                    <a
                      href="https://www.linkedin.com/company/clumen/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/45 hover:text-white transition-colors duration-500"
                      aria-label="LinkedIn"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                    <a
                      href="https://www.instagram.com/codelumen"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/45 hover:text-white transition-colors duration-500"
                      aria-label="Instagram"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                      </svg>
                    </a>
                    <span className="font-sans text-white/30 text-[10px] tracking-[0.2em]">© 2026 codelumen</span>
                  </div>
                </motion.div>
              </motion.div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
